// ConsentGate — intercepts the authed app for any user who hasn't
// accepted the current POLICY_VERSION *or*, for a customer administrator,
// TERMS_VERSION yet. Renders a full-screen
// modal with a summary of the policy, a deep link to the full text,
// and an "I agree" button that calls record_user_consent then
// invalidates ['me'] so the gate unmounts itself.
//
// Sits inside App.tsx wrapping the authed-routes subtree, after
// LockoutGate (you can't accept the policy if your account is locked
// anyway) and before TenantStatusGate (a suspended tenant still needs
// to see the policy if they ever come back).
//
// AcceptInvite records consent inline: its tick-box covers BOTH documents and
// writes both version keys through record_user_consent. The invite link mints
// the session before the set-password form shows, so the page renders INSIDE
// this gate — and the gate stands aside there (Jason's ruling, 5 Oct: one
// acceptance on the invite, not the gate's and then the form's). Leaving the
// form without accepting lands on a gated path like any other. The gate exists
// for:
//   - Users who signed up before POPIA capture was wired (legacy).
//   - EVERY customer ADMINISTRATOR who predates the Terms of Service
//     (TERMS_EFFECTIVE_DATE). Administrators accept the Terms on behalf of
//     their business (Jason's ruling, 6 Oct — acceptsTermsForBusiness in
//     terms.ts); nobody else is ever asked for them. Until the terms shipped
//     there was no document to accept, so every existing administrator sees
//     this gate exactly once. That interrupt is the point: a term nobody
//     accepted binds nobody.
//   - Future POLICY_VERSION / TERMS_VERSION bumps — every active user
//     re-consents to whichever document changed.
//   - Edge case where the AcceptInvite RPC call failed (caught + logged
//     there; we re-prompt here so consent is still captured).
//
// The two documents it asks for are NOT behind it. "Read the full Terms of
// Service" opens /terms in a new tab, and that tab is signed in too — so a
// gate that covered every path covered the very text it asks you to accept,
// and the link opened a second copy of the modal. A term the person could not
// read before agreeing is weak evidence of agreement; the pages are public
// pre-auth anyway (ECTA s43(1)), so letting them through costs nothing.
//
// ⚠️ THE PROMPT ALWAYS HAS A WAY OUT THAT IS NOT "I AGREE". It renders INSTEAD
// OF the app, so on a shared tablet a worker who walked away left the device
// signed in as them, under the prompt — and the next person's only exit was to
// accept in the first worker's name. Sign out takes the same path as the idle
// sign-out (src/lib/signOutThisDevice.ts): a paired device goes back to its
// badge + face lock screen, anything else to sign-in, and it works with no
// connection. The idle sign-outs themselves are mounted OUTSIDE this gate
// (<SharedDeviceSignOuts/> in App.tsx), so they still fire under the prompt.
// Who must accept, and when, is unchanged: Sign out records nothing.

import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { UNATTENDED_DISPLAY_PATHS } from '@/lib/unattendedDisplayPaths'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Shield, ExternalLink, LogOut } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { withTimeout } from '@/lib/saveTimeout'
import { consentFailureText } from '@/lib/consentFailure'
import { signOutThisDevice } from '@/lib/signOutThisDevice'
import {
  POLICY_VERSION,
  POLICY_VERSION_LABEL,
  RESPONSIBLE_PARTY,
} from '@/lib/popia'
import { TERMS_VERSION, TERMS_VERSION_LABEL, acceptsTermsForBusiness } from '@/lib/terms'

// The two documents this gate can ask for. Order is the order they appear in
// the modal: the Terms are the newer ask and the one most users are here for.
const DOCUMENTS = [
  {
    version: TERMS_VERSION,
    label:   TERMS_VERSION_LABEL,
    title:   'Terms of Service',
    path:    '/terms',
    blurb:   'are accepted by you, as an administrator, on behalf of your business. They '
           + 'cover its subscription, what the Platform may be used for, who owns '
           + 'the data you enter, and the limits of our liability. Note in particular '
           + 'that pay figures must be checked by your business before a run is paid, '
           + 'and that advisory signals are decision support — never a substitute for '
           + 'a product label.',
  },
  {
    version: POLICY_VERSION,
    label:   POLICY_VERSION_LABEL,
    title:   'Privacy Policy',
    path:    '/privacy',
    blurb:   'explains what personal information we process, why, who we share it '
           + "with, how long we keep it, and your rights as a data subject under "
           + "South Africa's POPIA.",
  },
] as const

// Which documents a user still owes, and the copy that names them. Pure, and
// deliberately OUTSIDE the component: naming the Privacy Policy at someone who
// only owes the Terms (or the reverse) is the kind of small untruth that makes
// a consent record worth less than it looks, so the mapping is worth reading
// on its own rather than inline among the hooks.
function consentCopy(missing: readonly string[]) {
  const owed = DOCUMENTS.filter(d => missing.includes(d.version))
  return {
    owed,
    // "Privacy Policy update" only when the policy is the sole outstanding
    // document — for a returning user that IS what changed. Any ask that
    // includes the Terms is a first acceptance, not an update.
    heading: owed.length === 1 && owed[0].version === POLICY_VERSION
      ? 'Privacy Policy update'
      : owed.map(d => d.title).join(' & '),
    versionLine: owed.map(d => d.label).join(' · '),
  }
}

// The paths the gate never covers: the documents it links to. Derived from
// DOCUMENTS so a third document is readable the day it is asked for.
const DOCUMENT_PATHS: ReadonlySet<string> = new Set(DOCUMENTS.map(d => d.path))

// The invite / password-reset form. It takes the same acceptance itself — a
// tick-box naming both documents, both keys written, context 'invite_accept'
// — so the gate over it would only ask the same person the same thing twice.
// Exact path only: nothing else on the signed-in side is reachable from here
// without passing the gate.
const SELF_CONSENTING_PATHS: ReadonlySet<string> = new Set(['/accept-invite'])

// Screens nobody sits at: the floor TV and the clock kiosk opened from the
// Clock tile. A TV left signed in as an administrator would otherwise switch
// to the one-time Terms prompt with nobody there to read it, and stay on it —
// neither route is a ?kiosk=1 or paired device, so no idle sign-out clears it.
// They never ask for the TERMS (Jason's ruling, 7 Oct): the administrator
// accepts the next time they open any other page. The privacy prompt is
// unchanged here. Exact paths, like SELF_CONSENTING_PATHS.

// Which documents a user still owes, from the cached ['me'] consents. Each
// version is a separate top-level key with its own __history, so re-accepting
// one never disturbs the other's evidence. The Terms are owed only by an
// administrator of the active tenant — for anyone else they are not asked for
// at all, so a worker with the privacy policy accepted passes straight
// through. Nor on an unattended display screen — see UNATTENDED_DISPLAY_PATHS.
// A falsy value (null, '', a __proto__-mangled entry) is not an acceptance.
function owedVersions(
  consents: Record<string, unknown> | null | undefined,
  tenantRole: Parameters<typeof acceptsTermsForBusiness>[0],
  pathname: string,
): string[] {
  const asksTerms = acceptsTermsForBusiness(tenantRole) && !UNATTENDED_DISPLAY_PATHS.has(pathname)
  return DOCUMENTS
    .filter(d => d.version !== TERMS_VERSION || asksTerms)
    .map(d => d.version)
    .filter(v => !consents?.[v])
}

// The paths the gate stands aside on whatever is owed: a document it links to
// (see the header note) and the invite form that takes the acceptance itself.
function standsAside(pathname: string): boolean {
  return DOCUMENT_PATHS.has(pathname) || SELF_CONSENTING_PATHS.has(pathname)
}

export function ConsentGate({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading } = useCurrentUser()
  const { pathname } = useLocation()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [signingOut, setSigningOut] = useState(false)
  // Local override: as soon as the RPC succeeds we set this and pass the
  // gate immediately. Initial bug (2026-05-26): the qc.invalidateQueries
  // refetch didn't propagate to the gate within the same tick (still
  // diagnosing), so users clicked "I agree" 20+ times each — every click
  // wrote a consent row but the modal stayed up. This belt-and-braces
  // local flag means the modal unmounts the moment the RPC returns
  // regardless of cache state.
  const [acceptedLocally, setAcceptedLocally] = useState(false)

  const missing = owedVersions(me?.consents as Record<string, unknown> | null | undefined, me?.tenant_role, pathname)

  const accept = useMutation({
    mutationFn: async () => {
      // Record ONLY what is missing. A user who already accepted the privacy
      // policy and is here purely for the terms must not have their original
      // privacy acceptance rewritten — record_user_consent would push the
      // real one into __history and stamp a fresh accepted_at, which quietly
      // destroys the date the acceptance actually happened on.
      for (const version of missing) {
        // Bounded: on a dead link the call can hang, and "Recording…" forever
        // reads as a frozen app. The timeout reads as no connection below.
        const { error: rpcErr } = await withTimeout(supabase.rpc('record_user_consent', {
          p_policy_version: version,
          p_context:        'consent_gate',
          p_ip:             null,
          p_user_agent:     navigator.userAgent.slice(0, 200),
        }), 'ConsentGate: record_user_consent')
        // The raw error, not a re-wrapped message: consentFailureText needs its
        // code and text to tell no-connection from a real refusal.
        if (rpcErr) throw rpcErr
      }
    },
    onSuccess: () => {
      setAcceptedLocally(true)
      // Kick the cache so the next render of every other ['me'] consumer
      // sees the new consents. Not load-bearing for THIS modal closing
      // (the local flag does that) but keeps the rest of the app in sync.
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError:   (e: unknown) => {
      setError(consentFailureText(e))
      // Two documents are two writes: if the first landed and the second
      // failed, refetch so a retry records only what is still owed (a repeat
      // would push the first acceptance into __history and re-stamp its date).
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })

  // Never disabled by a pending accept: a hung "Recording…" is exactly when
  // the person needs the way out.
  async function signOut() {
    setSigningOut(true)
    await signOutThisDevice(navigate)
  }

  // While useCurrentUser is loading, let the rest of the app render —
  // other gates handle their own loading states and we don't want a
  // double-spinner. If the user isn't logged in at all (`me === null`)
  // ConsentGate is a no-op too; the unauthed route table handles them.
  if (isLoading || !me) return <>{children}</>

  // Local override wins — see acceptedLocally above. Then the paths the gate
  // never covers, then the hot path: nothing owed.
  if (acceptedLocally || standsAside(pathname) || missing.length === 0) return <>{children}</>

  // Cold path: gate the app — see ConsentPrompt.
  return (
    <ConsentPrompt
      missing={missing}
      error={error}
      agreeing={accept.isPending}
      onAgree={() => accept.mutate()}
      signingOut={signingOut}
      onSignOut={() => { void signOut() }} />
  )
}

// The prompt itself. Body scroll is locked by the fixed inset-0 overlay;
// nothing else is reachable until the acceptance lands or the person signs out.
function ConsentPrompt({ missing, error, agreeing, onAgree, signingOut, onSignOut }: {
  missing:    readonly string[]
  error:      string | null
  agreeing:   boolean
  onAgree:    () => void
  signingOut: boolean
  onSignOut:  () => void
}) {
  const { owed, heading, versionLine } = consentCopy(missing)
  return (
    <div className="fixed inset-0 z-overlay bg-gray-950/95 flex items-center justify-center p-4">
      <div className="surface-sunk border hairline-strong rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col overflow-hidden">
        <header
          className="px-5 pb-4 border-b hairline flex items-center gap-3"
          style={{ paddingTop: 'max(env(safe-area-inset-top), 1.25rem)' }}>
          <Shield size={22} className="text-emerald-400" />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-fg">{heading}</h2>
            <p className="text-[11px] text-fg-muted">
              {versionLine} · {RESPONSIBLE_PARTY.legalName}
            </p>
          </div>
        </header>

        <div className="px-5 py-4 overflow-y-auto text-sm text-fg-secondary space-y-3">
          <p>
            Before continuing, please review and accept {owed.length > 1 ? 'the documents' : 'the document'} below.
          </p>
          {owed.map(doc => (
            <div key={doc.version} className="space-y-1">
              <p className="text-fg-muted text-xs">
                Our <b className="text-fg-secondary">{doc.title}</b> {doc.blurb}
              </p>
              <p>
                <Link to={doc.path} target="_blank" rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
                  Read the full {doc.title} <ExternalLink size={11} />
                </Link>
              </p>
            </div>
          ))}
          <ul className="list-disc pl-5 space-y-1 text-fg-muted text-xs">
            <li>Responsible Party: <b className="text-fg-secondary">{RESPONSIBLE_PARTY.legalName}</b></li>
            <li>Information Officer: <b className="text-fg-secondary">{RESPONSIBLE_PARTY.informationOfficer.name}</b></li>
            <li>Privacy contact: <a href={`mailto:${RESPONSIBLE_PARTY.contactEmail}`} className="text-blue-400 hover:text-blue-300">{RESPONSIBLE_PARTY.contactEmail}</a></li>
            <li>You can request a copy of your data, ask for corrections, or withdraw consent at any time from <Link to="/privacy/my-data" className="text-blue-400 hover:text-blue-300">/privacy/my-data</Link>.</li>
          </ul>
          {error && (
            <div className="rounded-xl bg-red-900/50 border border-red-700 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}
        </div>

        <footer className="px-5 py-3 border-t hairline flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            className="btn-secondary btn-sm mr-auto inline-flex items-center gap-1.5">
            <LogOut size={14} aria-hidden="true" />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
          <button
            type="button"
            onClick={onAgree}
            disabled={agreeing}
            className="btn-primary btn-sm">
            {agreeing ? 'Recording…' : 'I agree & continue'}
          </button>
        </footer>
      </div>
    </div>
  )
}
